/**
 * LoomPlanReview — inline pre-spine view that shows the Opus planner's
 * proposed retrieval plan to the writer for approval.
 *
 * Phase 8a (planner integration). Renders BETWEEN the idle picker
 * and the spine: when the writer clicks Start Draft, the shell calls
 * createPlan() first; this view shows the plan; on Approve the shell
 * calls startRun({plan_id}) and the spine takes over.
 *
 * Variant C colour vocabulary:
 *   - Step callout: brand purple — "current focus" (this is step 5)
 *   - Summary cards: paper-dim with brand-mid icon
 *   - Gaps: amber (warning) or red (blocker), per severity
 *   - Approve CTA: brand purple primary
 *
 * SRP: pure presentation. PlanData shape mirrors the classic UI's
 * (POST /api/plan response). The shell owns lifecycle (loading,
 * approve/modify/skip dispatch); this component just renders + emits.
 *
 * Why we don't reuse the classic PlanReview component verbatim:
 * its styling uses var(--gray-*) tokens that don't match the
 * Variant C design language. Same data, different presentation.
 * Future: lift PlanData type to a shared types module so the two
 * components don't drift independently.
 */

import { Spinner, makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { derivePlanHighlights, derivePlanNarrative, formatPlanSubline } from "../planNarrative";
import type { WriteStrategy } from "../selection";
import { brandColors, fonts, gateColorAt, inkColors, statusColors, surfaceColors } from "../tokens";

// ---------------------------------------------------------------------------
// Data shape — mirrors classic PlanReview's PlanData (POST /api/plan)
// ---------------------------------------------------------------------------

export interface PlanData {
  summary: {
    module_count: number;
    total_planned_calls: number;
    shared_retrievals: number;
    calls_saved: number;
    estimated_seconds: number;
    gap_count: number;
  };
  modules: Array<{
    module_id: string;
    title: string;
    total_calls: number;
    shared_refs: string[];
    data_points: string[];
  }>;
  shared_retrievals: Array<{
    id: string;
    retrieval: { file_label: string; section_label: string };
    used_by: string[];
  }>;
  gaps: Array<{
    document_type: string;
    needed_by: string[];
    impact: string;
    severity: string;
  }>;
}

interface LoomPlanReviewProps {
  plan: PlanData | null;
  loading: boolean;
  /** Backend API health for the planner — when false, the plan is
   *  unreliable and we surface a warning + offer Skip. */
  apiHealthy: boolean;
  /** Plan-fetch or planner error — null in the happy path. */
  error: string | null;
  /** The doc + library the writer picked — surfaced in the header
   *  so the writer can confirm "yes, this is what I'm planning for." */
  docCode: string;
  libraryName: string;
  /**
   * Approve handler — receives the writer's selected module_ids.
   * When the writer selects ALL modules, ``moduleIds`` equals
   * ``plan.modules.map(m => m.module_id)``; the shell uses this to
   * decide whether to pass module_ids to startRun (subset) or omit it
   * (full plan). Either way the planId is part of the same approval.
   */
  onApprove: (moduleIds: string[]) => void;
  onModify: () => void;
  /** Optional skip — bypasses the plan, starts a run with no plan_id.
   *  Useful when the planner is degraded but the writer wants to proceed. */
  onSkip: () => void;
  /** Writer's write-strategy choice from LoomSelection (Spec 23 P10.4).
   *  When provided, the "Planner says" narrative adds a one-line
   *  preview of how the run will feel at runtime. Optional so the
   *  legacy-fallback path (no LoomSelection commit) still renders
   *  without a strategy-specific sentence. */
  strategy?: WriteStrategy;
  /** Spec 23 PR-A — gate definitions from the doctype config. When
   *  provided, plan modules render in coloured gate packets
   *  matching the picker. Omit (legacy-fallback) → flat list. */
  gates?: Array<{ name: string; display_name: string; modules: string[] }>;
  /** Spec 23 PR-A — count of modules pulled in by the picker's
   *  dependency resolver (parent + dep auto-include). Drives the
   *  PlanHighlights "deps pulled in" stat. Defaults to 0. */
  impliedCount?: number;
  /** Spec 23 PR-A — back-to-picker handler for the new compact
   *  header pill. When omitted, the back button is hidden (legacy
   *  fallback). Equivalent to ``onModify`` semantically; named
   *  separately to keep the new picker-aware UX explicit. */
  onBackToPicker?: () => void;
  /** Phase 9.4 — module ids already drafted in a prior run. In the
   *  "Draft more" flow these render with a "Drafted" tag and start
   *  DESELECTED (never auto-included), so nothing is overwritten by
   *  default. The writer can opt-in to re-draft one via its row toggle
   *  ("Overwrite"); doing so passes it to ``onApprove`` and the backend
   *  continue path resets + re-drafts it. Default empty → legacy "all
   *  modules actionable" behaviour, no change for the fresh-draft path. */
  lockedModuleIds?: ReadonlyArray<string>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSeconds(s: number): string {
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const rs = Math.round(s % 60);
  return rs > 0 ? `${m}m ${rs}s` : `${m}m`;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    background: surfaceColors.paper,
    padding: "20px 18px 24px",
    fontFamily: fonts.body,
  },
  // Step 5 of 5 callout — brand purple, same shape as LoomFresh's
  // step callouts so the writer reads continuity.
  stepCallout: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "10px 12px",
    background: brandColors.light,
    border: `1px solid ${brandColors.primary}`,
    borderLeft: `3px solid ${brandColors.primary}`,
    borderRadius: "4px",
    marginBottom: "12px",
  },
  stepCalloutHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
  },
  stepCalloutBadge: {
    fontSize: "9px",
    fontWeight: 800,
    letterSpacing: "0.6px",
    color: "#fff",
    background: brandColors.primary,
    padding: "2px 6px",
    borderRadius: "2px",
    fontFamily: fonts.mono,
    textTransform: "uppercase",
  },
  stepCalloutTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: inkColors.ink,
    fontFamily: fonts.display,
  },
  stepCalloutHint: {
    fontSize: "11px",
    color: inkColors.ink3,
    lineHeight: 1.4,
  },
  contextLine: {
    fontSize: "10.5px",
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    marginBottom: "10px",
  },
  // Summary stats grid
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "6px",
    marginBottom: "12px",
  },
  summaryCard: {
    padding: "8px 10px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
  },
  summaryLabel: {
    fontSize: "8.5px",
    fontWeight: 700,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: "2px",
  },
  summaryValue: {
    fontSize: "16px",
    fontWeight: 700,
    color: inkColors.ink,
    fontFamily: fonts.display,
    fontVariantNumeric: "tabular-nums",
  },
  summaryUnit: {
    fontSize: "10px",
    fontWeight: 500,
    color: inkColors.ink4,
    marginLeft: "4px",
  },
  // Spec 23 P10.4 — "Planner says" narrative block. Sits between the
  // numeric summary grid and the module list; small label + prose body
  // styled as a quiet callout (no strong colour, no border heavy
  // enough to compete with the module list).
  plannerSays: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    padding: "8px 10px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderLeft: `3px solid ${brandColors.mid}`,
    borderRadius: "3px",
    marginBottom: "12px",
  },
  plannerSaysLabel: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: inkColors.ink4,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  plannerSaysBody: {
    fontSize: "11.5px",
    color: inkColors.ink2,
    lineHeight: 1.5,
  },
  // -- Spec 23 PR-A: new compact header + PlanHighlights + Skip rows ----
  newHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    paddingBottom: "10px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    marginBottom: "12px",
  },
  newHeaderTitleBlock: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  newHeaderTitle: {
    fontSize: "11px",
    fontWeight: 700,
    color: inkColors.ink,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  newHeaderSubline: {
    fontSize: "10.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
  },
  backToPickerBtn: {
    fontSize: "10px",
    fontFamily: fonts.body,
    fontWeight: 600,
    color: inkColors.ink3,
    background: "#fff",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "10px",
    padding: "3px 10px",
    cursor: "pointer",
    flexShrink: 0,
    "&:hover": {
      color: inkColors.ink,
      border: `1px solid ${brandColors.primary}`,
    },
  },
  highlightsCard: {
    padding: "12px",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    marginBottom: "12px",
  },
  highlightsLabel: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  highlightsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "10px",
    marginBottom: "10px",
  },
  highlightCell: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  highlightNumber: {
    fontSize: "18px",
    fontWeight: 700,
    fontFamily: fonts.body,
    lineHeight: 1.1,
  },
  highlightLabel: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
  },
  highlightsNarrative: {
    fontSize: "11px",
    color: inkColors.ink2,
    lineHeight: 1.5,
  },
  // Gate-packet styles — minimal port of LoomSelection's GatePacket
  // for the plan-review surface. Future refactor could lift the
  // packet into a shared component; for now the duplication is
  // bounded + the styles are small.
  packet: {
    position: "relative",
    margin: "0 0 12px",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  packetStripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "4px",
  },
  packetHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px 8px 16px",
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  packetLabel: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
  },
  packetCount: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  // Plan-step row (Skip/Undo treatment)
  stepRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 12px",
    paddingLeft: "16px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  stepRowSkipped: {
    opacity: 0.45,
  },
  // Phase 9.4 — locked-row treatment for already-drafted sections
  // surfaced in a follow-up "Draft more sections" picker. Lower
  // opacity than skipped (writer doesn't need to re-engage with
  // these), brand-tinted background to read as "done, locked"
  // rather than "skipped". The Skip button slot is replaced by an
  // inline ``Drafted`` label.
  stepRowLocked: {
    opacity: 0.7,
    background: brandColors.light,
  },
  stepLockedLabel: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: brandColors.primary,
    padding: "2px 8px",
    borderRadius: "4px",
    flexShrink: 0,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  stepNumber: {
    width: "28px",
    fontSize: "10px",
    color: inkColors.ink4,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontFamily: fonts.mono,
    flexShrink: 0,
  },
  stepCircle: {
    width: "12px",
    height: "12px",
    borderRadius: "6px",
    background: "#fff",
    flexShrink: 0,
  },
  stepBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  stepTitle: {
    fontSize: "12px",
    fontWeight: 500,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  stepTitleSkipped: {
    textDecoration: "line-through",
  },
  stepMeta: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  skipBtn: {
    border: "none",
    background: "transparent",
    fontSize: "10px",
    fontFamily: fonts.body,
    fontWeight: 600,
    color: inkColors.ink4,
    cursor: "pointer",
    padding: "2px 8px",
    borderRadius: "4px",
    flexShrink: 0,
    "&:hover": {
      color: brandColors.primary,
      background: brandColors.light,
    },
  },
  skipBtnUndo: {
    color: brandColors.primary,
  },
  // Footer (new 2-button)
  newFooter: {
    display: "flex",
    gap: "8px",
    paddingTop: "12px",
    borderTop: `1px solid ${surfaceColors.line}`,
    marginTop: "12px",
  },
  newCancelBtn: {
    padding: "9px 14px",
    fontSize: "11.5px",
    fontWeight: 600,
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: "#fff",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.body,
  },
  newPrimaryBtn: {
    flex: 1,
    padding: "9px 14px",
    fontSize: "11.5px",
    fontWeight: 700,
    borderRadius: "6px",
    border: "none",
    background: brandColors.primary,
    color: "#fff",
    cursor: "pointer",
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    boxShadow: `0 2px 6px ${brandColors.primary}33`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  newPrimaryBtnDisabled: {
    background: surfaceColors.lineStrong,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  sectionTitle: {
    fontSize: "9.5px",
    fontWeight: 700,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.7px",
    marginTop: "14px",
    marginBottom: "6px",
  },
  // Header strip above the module list — selection summary +
  // bulk-toggle links. Lives inside the module-list container so
  // it scrolls with the list rather than being a fixed header.
  moduleListHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    marginBottom: "6px",
    fontSize: "10.5px",
    color: inkColors.ink3,
  },
  moduleListCount: {
    fontFamily: fonts.mono,
    fontVariantNumeric: "tabular-nums",
    color: inkColors.ink2,
  },
  bulkToggle: {
    fontSize: "10.5px",
    color: brandColors.primary,
    fontWeight: 600,
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
  },
  bulkToggleSep: {
    color: inkColors.ink4,
  },
  // Subset warning — surfaces ONLY when the writer has deselected
  // some modules. Honest copy: the plan was made for the full set;
  // running a subset may include a few unnecessary retrievals.
  subsetBanner: {
    marginTop: "10px",
    padding: "8px 10px",
    background: surfaceColors.paperDim,
    borderLeft: `2px solid ${brandColors.mid}`,
    borderRadius: "3px",
    fontSize: "10.5px",
    color: inkColors.ink3,
    lineHeight: 1.4,
  },
  moduleList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  moduleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 10px",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
    fontSize: "11.5px",
    color: inkColors.ink2,
    cursor: "pointer",
  },
  moduleRowDeselected: {
    background: surfaceColors.paperDim,
    color: inkColors.ink4,
  },
  moduleCheckbox: {
    width: "13px",
    height: "13px",
    cursor: "pointer",
    flexShrink: 0,
  },
  moduleId: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: brandColors.primary,
    fontWeight: 700,
    minWidth: "60px",
  },
  moduleTitle: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  moduleCalls: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
  },
  // Gaps — warning when soft, blocker when hard
  gapsBox: {
    marginTop: "10px",
    padding: "8px 10px",
    background: "#FFFBEB",
    border: `1px solid ${statusColors.warn}`,
    borderLeft: `3px solid ${statusColors.warn}`,
    borderRadius: "3px",
  },
  gapsBoxBlocker: {
    background: "#FEF2F2",
    border: `1px solid ${statusColors.danger}`,
    borderLeft: `3px solid ${statusColors.danger}`,
  },
  gapHeader: {
    fontSize: "10px",
    fontWeight: 700,
    color: "#7C2D12",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "4px",
  },
  gapHeaderBlocker: {
    color: "#7F1D1D",
  },
  gapRow: {
    fontSize: "11px",
    color: inkColors.ink2,
    marginBottom: "3px",
    lineHeight: 1.4,
  },
  // Action footer
  actionRow: {
    display: "flex",
    gap: "8px",
    marginTop: "16px",
    paddingTop: "12px",
    borderTop: `1px solid ${surfaceColors.line}`,
  },
  primaryBtn: {
    flex: 1,
    fontSize: "12px",
    fontWeight: 700,
    color: "#fff",
    background: brandColors.primary,
    border: "none",
    padding: "9px 16px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  primaryBtnDisabled: {
    background: surfaceColors.lineStrong,
    color: inkColors.ink4,
    cursor: "not-allowed",
  },
  secondaryBtn: {
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink2,
    background: "#fff",
    border: `1px solid ${surfaceColors.lineStrong}`,
    padding: "8px 12px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
  },
  // Loading + error overlays
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "40px 16px",
    textAlign: "center",
  },
  loadingTitle: {
    fontFamily: fonts.display,
    fontSize: "14px",
    fontWeight: 700,
    color: brandColors.primary,
  },
  loadingHint: {
    fontSize: "11px",
    color: inkColors.ink3,
    maxWidth: "320px",
    lineHeight: 1.5,
  },
  errorBanner: {
    marginBottom: "12px",
    padding: "10px",
    background: "#FEF2F2",
    border: `1px solid ${statusColors.danger}`,
    borderRadius: "3px",
    fontSize: "11.5px",
    color: "#7F1D1D",
    lineHeight: 1.5,
  },
  apiUnhealthyBanner: {
    marginBottom: "10px",
    padding: "8px 10px",
    background: "#FFFBEB",
    border: `1px solid ${statusColors.warn}`,
    borderLeft: `3px solid ${statusColors.warn}`,
    borderRadius: "3px",
    fontSize: "11px",
    color: inkColors.ink2,
    lineHeight: 1.4,
  },
  elapsed: {
    fontSize: "10.5px",
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    fontVariantNumeric: "tabular-nums",
  },
});

// ---------------------------------------------------------------------------
// LoadingElapsed — "am I waiting too long?" signal while the planner streams
// ---------------------------------------------------------------------------
//
// # DECISION: ticks at 1s resolution client-side, started when the
// #   component mounts into loading state.
// # WHY: the backend doesn't emit mid-flight progress events today (that
// #      would be a hot-path change, see Spec 18 proposal). Elapsed-time
// #      on the client is the honest minimum — writer can see if the
// #      planner has silently hung without us inventing fake progress.
// # DATE: 2026-04-21
//
// SRP: renders a monotonic "elapsed" counter. No network, no state
// beyond the tick. Resets automatically by virtue of mounting fresh
// on each plan-review entry.

const LoadingElapsed: React.FC = () => {
  const styles = useStyles();
  const [elapsedMs, setElapsedMs] = useState(0);
  const startedAt = useMemo(() => Date.now(), []);
  useEffect(() => {
    const id = window.setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);
  const totalSec = Math.floor(elapsedMs / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  const label = m > 0 ? `${m}m ${String(s).padStart(2, "0")}s elapsed` : `${s}s elapsed`;
  return (
    <div className={styles.elapsed} aria-live="polite">
      {label}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LoomPlanReview: React.FC<LoomPlanReviewProps> = ({
  plan,
  loading,
  apiHealthy,
  error,
  docCode,
  libraryName,
  onApprove,
  onModify,
  onSkip,
  strategy,
  gates,
  impliedCount = 0,
  onBackToPicker,
  lockedModuleIds,
}) => {
  const styles = useStyles();

  // Phase 9.4 — stable Set for fast membership checks. Computed
  // once per ``lockedModuleIds`` change. Empty Set when caller
  // passes nothing → ``isLocked()`` returns false uniformly,
  // preserving the legacy fresh-draft behaviour.
  const locked = useMemo<Set<string>>(() => new Set(lockedModuleIds ?? []), [lockedModuleIds]);

  // -- Per-module selection state ----------------------------------------
  // Default: every NON-LOCKED module starts ticked (writer's baseline
  // intent — draft what's left). Locked = already-drafted modules; they
  // start DESELECTED so nothing is overwritten by default, but the
  // writer can opt-in to re-draft one via its row toggle.
  // Re-syncs whenever the plan or locked-set changes.
  const allModuleIds = useMemo<string[]>(() => (plan ? plan.modules.map((m) => m.module_id) : []), [plan]);
  const actionableModuleIds = useMemo<string[]>(
    () => allModuleIds.filter((id) => !locked.has(id)),
    [allModuleIds, locked],
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set(actionableModuleIds));

  // Reset on plan OR locked-set change. Compare via length+first-id
  // signature — cheap, avoids deep-equal; same pattern as
  // useVariantCRun. Including locked.size in the signature makes the
  // reset fire when a follow-up run reseeds the picker with a new
  // lock list (Phase 9.4 — "Draft more sections" flow).
  const planSignature = useMemo(
    () => `${allModuleIds.length}:${allModuleIds[0] ?? ""}:${locked.size}`,
    [allModuleIds, locked],
  );
  useEffect(() => {
    void planSignature; // trigger: only reset when plan composition changes
    setSelected(new Set(actionableModuleIds));
  }, [planSignature, actionableModuleIds]);

  const toggleModule = (moduleId: string): void => {
    // Drafted rows (in ``locked``) are OPT-IN overwritable in the
    // "Draft more" flow: they start deselected (never in
    // ``actionableModuleIds``) but the writer can tick one to re-draft
    // it. Nothing is overwritten unless the writer explicitly
    // re-includes it here, so no guard is needed.
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };
  // Phase 9.4 — select-all targets only the actionable (non-locked)
  // set. Same effective behaviour as legacy when no lock list is
  // passed (actionableModuleIds === allModuleIds).
  const selectAll = (): void => setSelected(new Set(actionableModuleIds));
  const selectNone = (): void => setSelected(new Set());

  const selectedCount = selected.size;
  const totalCount = actionableModuleIds.length;
  const isSubset = selectedCount > 0 && selectedCount < totalCount;
  const isEmpty = selectedCount === 0;

  // Loading state — first paint while createPlan() is in flight.
  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.centered}>
          <Spinner size="medium" />
          <div className={styles.loadingTitle}>Planning your draft…</div>
          <div className={styles.loadingHint}>
            Asthra is reading {libraryName} and deciding which sources each section needs. Large doc types (full CSR)
            take 4–6 minutes; smaller ones are faster. The planner runs once; after approval the draft starts
            immediately.
          </div>
          <LoadingElapsed />
        </div>
      </div>
    );
  }

  // Hard error — planner couldn't return anything actionable. Offer
  // Modify (go back to picker) or Skip (start without a plan).
  if (error || !plan) {
    return (
      <div className={styles.root}>
        <div className={styles.errorBanner}>
          <strong>Couldn&apos;t plan this draft.</strong> {error ?? "The planner returned no plan."}
        </div>
        <div className={styles.actionRow}>
          <button type="button" className={styles.secondaryBtn} onClick={onModify}>
            Back to picker
          </button>
          <button type="button" className={styles.primaryBtn} onClick={onSkip}>
            Skip plan &amp; draft anyway
          </button>
        </div>
      </div>
    );
  }

  const hasBlockerGaps = plan.gaps.some((g) => (g.severity || "").toLowerCase() === "high");

  // -- Spec 23 PR-A — derived bits for the new layout ------------------
  const highlights = derivePlanHighlights(plan, impliedCount);
  const subline = formatPlanSubline(highlights.stepCount, highlights.totalMin, strategy);
  const narrative = derivePlanNarrative(plan, strategy);

  // Gate grouping when ``gates`` prop provided. Each group keeps its
  // declared order; a fallback "Other" group catches any plan.modules
  // not mapped to a configured gate (defensive — shouldn't happen in
  // practice). Without ``gates``, render flat with no packet chrome.
  const moduleGroups: Array<{
    gateName: string;
    gateDisplayName: string;
    gateColor: string;
    modules: PlanData["modules"];
  }> = (() => {
    if (!gates || gates.length === 0) {
      return [
        {
          gateName: "all",
          gateDisplayName: "Plan",
          gateColor: brandColors.mid,
          modules: plan.modules,
        },
      ];
    }
    const accountedFor = new Set<string>();
    const groups = gates
      .map((g, i) => {
        const gateModuleIds = new Set(g.modules ?? []);
        const mods = plan.modules.filter((m) => {
          if (gateModuleIds.has(m.module_id)) {
            accountedFor.add(m.module_id);
            return true;
          }
          return false;
        });
        return {
          gateName: g.name,
          gateDisplayName: g.display_name,
          gateColor: gateColorAt(i),
          modules: mods,
        };
      })
      .filter((g) => g.modules.length > 0);
    const orphans = plan.modules.filter((m) => !accountedFor.has(m.module_id));
    if (orphans.length > 0) {
      groups.push({
        gateName: "other",
        gateDisplayName: "Other",
        gateColor: inkColors.ink4,
        modules: orphans,
      });
    }
    return groups;
  })();

  // Map module_id → downstream-reference count so we can render
  // "reads §X, §Y · ~Nm" subline on each step row from real data.
  const downstreamRefs = new Map<string, string[]>();
  for (const m of plan.modules) {
    for (const ref of m.shared_refs ?? []) {
      const list = downstreamRefs.get(ref) ?? [];
      list.push(m.module_id);
      downstreamRefs.set(ref, list);
    }
  }

  return (
    <div className={styles.root}>
      {/* Spec 23 PR-A — compact header + back-to-picker pill. */}
      <output className={styles.newHeader}>
        <div className={styles.newHeaderTitleBlock}>
          <span className={styles.newHeaderTitle}>Plan ready · review &amp; edit before start</span>
          <span className={styles.newHeaderSubline}>{subline}</span>
        </div>
        {onBackToPicker && (
          <button type="button" className={styles.backToPickerBtn} onClick={onBackToPicker}>
            ← Back to picker
          </button>
        )}
      </output>

      {!apiHealthy && (
        <div className={styles.apiUnhealthyBanner}>
          <strong>Source library unreachable.</strong> The plan below may be incomplete — proceed only if you understand
          the gaps.
        </div>
      )}

      {/* PlanHighlights — 3-up big-number grid + narrative. */}
      <div className={styles.highlightsCard}>
        <div className={styles.highlightsLabel}>Planner says</div>
        <div className={styles.highlightsGrid}>
          <div className={styles.highlightCell}>
            <span className={styles.highlightNumber} style={{ color: inkColors.ink }}>
              {highlights.stepCount}
            </span>
            <span className={styles.highlightLabel}>
              section{highlights.stepCount === 1 ? "" : "s"} · ~{highlights.totalMin}m
            </span>
          </div>
          <div className={styles.highlightCell}>
            <span className={styles.highlightNumber} style={{ color: brandColors.primary }}>
              {highlights.impliedCount}
            </span>
            <span className={styles.highlightLabel}>deps pulled in</span>
          </div>
          <div className={styles.highlightCell}>
            <span className={styles.highlightNumber} style={{ color: statusColors.hot }}>
              {highlights.criticalCount}
            </span>
            <span className={styles.highlightLabel}>critical sections</span>
          </div>
        </div>
        {narrative && <div className={styles.highlightsNarrative}>{narrative}</div>}
      </div>

      {/* Gate-grouped step rows with Skip/Undo per row. */}
      {moduleGroups.map((group) => (
        <div key={group.gateName} className={styles.packet}>
          <div className={styles.packetStripe} style={{ background: group.gateColor }} />
          <div
            className={styles.packetHeader}
            style={{
              background: `${group.gateColor}08`,
              borderBottom: `1px solid ${group.gateColor}22`,
            }}
          >
            <span className={styles.packetLabel} style={{ color: group.gateColor }}>
              {group.gateDisplayName}
            </span>
            <span className={styles.packetCount}>
              {group.modules.length} section
              {group.modules.length === 1 ? "" : "s"}
            </span>
          </div>
          {group.modules.map((mod) => {
            // Draft-more: drafted rows are opt-in overwritable — they start
            // deselected (default-off) but the writer can tick one to
            // re-draft (overwrite) it. New rows behave as before.
            const isDrafted = locked.has(mod.module_id);
            const isIncluded = selected.has(mod.module_id);
            const isSkipped = !isIncluded;
            const reads = mod.shared_refs ?? [];
            const meta =
              reads.length > 0
                ? `reads §${reads.join(", §")} · ${mod.total_calls} call${mod.total_calls === 1 ? "" : "s"}`
                : `${mod.total_calls} call${mod.total_calls === 1 ? "" : "s"}`;
            return (
              <div
                key={mod.module_id}
                className={mergeClasses(
                  styles.stepRow,
                  isDrafted && styles.stepRowLocked,
                  isSkipped && styles.stepRowSkipped,
                )}
              >
                <span className={styles.stepNumber}>{mod.module_id}</span>
                <span
                  className={styles.stepCircle}
                  style={{
                    border: `1.5px solid ${group.gateColor}`,
                  }}
                  aria-hidden="true"
                />
                <span className={styles.stepBody}>
                  <span className={mergeClasses(styles.stepTitle, isSkipped && styles.stepTitleSkipped)}>
                    {mod.title}
                  </span>
                  <span className={styles.stepMeta}>{meta}</span>
                </span>
                {isDrafted && (
                  <span className={styles.stepLockedLabel} aria-label={`${mod.module_id} already drafted`}>
                    Drafted
                  </span>
                )}
                <button
                  type="button"
                  className={mergeClasses(styles.skipBtn, isSkipped && styles.skipBtnUndo)}
                  onClick={() => toggleModule(mod.module_id)}
                  title={
                    isDrafted
                      ? isSkipped
                        ? `Re-draft ${mod.module_id} — overwrites the existing draft`
                        : `Don't re-draft ${mod.module_id}`
                      : isSkipped
                        ? `Re-include ${mod.module_id}`
                        : `Skip ${mod.module_id}`
                  }
                >
                  {isDrafted ? (isSkipped ? "Overwrite" : "Skip") : isSkipped ? "Undo" : "Skip"}
                </button>
              </div>
            );
          })}
        </div>
      ))}

      {/* Gaps — kept from the prior layout; honest audit signal. */}
      {plan.gaps.length > 0 && (
        <div className={mergeClasses(styles.gapsBox, hasBlockerGaps && styles.gapsBoxBlocker)}>
          <div className={mergeClasses(styles.gapHeader, hasBlockerGaps && styles.gapHeaderBlocker)}>
            {plan.gaps.length} gap{plan.gaps.length === 1 ? "" : "s"} —
            {hasBlockerGaps ? " action recommended" : " consider adding sources"}
          </div>
          {plan.gaps.map((gap) => (
            <div key={gap.document_type} className={styles.gapRow}>
              <strong>{gap.document_type}</strong>: {gap.impact}
              {gap.needed_by.length > 0 && (
                <span style={{ color: inkColors.ink4 }}>
                  {" "}
                  · needed by {gap.needed_by.slice(0, 3).join(", ")}
                  {gap.needed_by.length > 3 ? `, +${gap.needed_by.length - 3} more` : ""}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Spec 23 PR-A — new 2-button footer. Cancel goes back to the
          picker (same handler as the header pill); Start drafting
          fires onApprove with the writer's non-skipped subset. */}
      <div className={styles.newFooter}>
        <button type="button" className={styles.newCancelBtn} onClick={onBackToPicker ?? onModify}>
          Cancel
        </button>
        <button
          type="button"
          className={mergeClasses(styles.newPrimaryBtn, isEmpty && styles.newPrimaryBtnDisabled)}
          onClick={() => onApprove(Array.from(selected))}
          disabled={isEmpty}
          title={
            isEmpty
              ? "Un-skip at least one section to draft"
              : `Draft ${selectedCount} section${selectedCount === 1 ? "" : "s"}`
          }
        >
          {isEmpty ? "Un-skip a section" : "Start drafting"}
          {!isEmpty && (
            <span
              style={{
                fontSize: "10px",
                opacity: 0.75,
                fontFamily: fonts.mono,
              }}
            >
              ↵
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default LoomPlanReview;
